import React, { useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  approveBankStatement,
  getBankStatementMappers,
  inspectBankStatement,
  previewBankStatement,
  saveBankStatementMapper,
} from "../../../../api/TransactionApisV2";
import type {
  BankStatementApprovalResult,
  BankStatementMapping,
  BankStatementMapper,
  BankStatementPayment,
  BankStatementPreview,
  BankStatementPreviewRow,
  BankStatementSystemField,
} from "../../../../types/Exercise";
import {
  Box,
  ButtonSize,
  Center,
  HStack,
  IconButtonType,
  IconCTAButton,
  IconSize,
  PrimaryCTAButton,
  TertiaryCTAButton,
  VStack,
} from "../../../../components/utils";
import { FileDropzone } from "../../../../components/shared/FileDropzone";
import { Instructions } from "../../../../components/shared/Instructions";
import {
  CheckBoxWithLabel,
  Input,
  Label,
} from "../../../../components/shared/InputField";
import SearchDropDown from "../../../../components/shared/SearchDropdown";
import { Select } from "../../../../components/shared/Select";
import Tooltip from "../../../../components/shared/Tooltip";
import { formatCurrency } from "../../../../utils/currencyFormatter";
import { formatDisplayDate } from "../../../../utils/date";

type Step = "UPLOAD" | "MAP" | "REVIEW";
type Selection = {
  paymentId: string;
  acknowledgeMismatch: boolean;
};

type ApprovalFailure = BankStatementApprovalResult["failedGroups"][number];

const emptyMapping: BankStatementMapping = {
  headerRow: 1,
  mapperData: {},
};

const bankStatementFieldLabels: Record<BankStatementSystemField, string> = {
  referenceId: "Reference ID",
  amount: "Amount",
  paymentDate: "Payment date",
};

const mappedSystemField = (mapping: BankStatementMapping, header: string) =>
  (Object.entries(mapping.mapperData).find(
    ([, field]) => field.field === header
  )?.[0] || "") as BankStatementSystemField | "";

const paymentLabel = (payment: BankStatementPayment, currencyType: string) =>
  `${payment.employeeName} (${
    payment.hrId || payment.requestId || "No ID"
  }) · ${payment.paymentType} · ${formatCurrency(
    payment.amount,
    currencyType
  )}`;

const rowStatusText: Record<string, string> = {
  ELIGIBLE: "Ready to approve",
  NEEDS_REVIEW: "Needs review",
  AMBIGUOUS: "Ambiguous",
  UNMATCHED: "Unmatched",
  INVALID: "Invalid row",
  EXCLUDED_DEBIT: "Excluded debit",
  ALREADY_APPROVED: "Already approved",
};

const sampleValue = (value: unknown) =>
  value === undefined || value === null || value === "" ? "-" : String(value);

const statementReferenceIncludesPaymentReference = (
  statementReference: string,
  paymentReference: string
) => {
  const normalizedPaymentReference = paymentReference
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  return (
    Boolean(normalizedPaymentReference) &&
    statementReference
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "")
      .includes(normalizedPaymentReference)
  );
};

export default function BankStatementReconciliationDialog({
  transactionId,
  currencyType,
  onClose,
  onSuccess,
}: {
  transactionId: string;
  currencyType: string;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
}) {
  const [step, setStep] = useState<Step>("UPLOAD");
  const [file, setFile] = useState<File>();
  const [headers, setHeaders] = useState<string[]>([]);
  const [uploadHeaderRow, setUploadHeaderRow] = useState(1);
  const [sampleData, setSampleData] = useState<Record<string, unknown>>({});
  const [mapping, setMapping] = useState<BankStatementMapping>(emptyMapping);
  const [savedMappers, setSavedMappers] = useState<BankStatementMapper[]>([]);
  const [selectedMapperId, setSelectedMapperId] = useState("");
  const [mapperName, setMapperName] = useState("");
  const [preview, setPreview] = useState<BankStatementPreview>();
  const [selections, setSelections] = useState<Record<string, Selection>>({});
  const [approvalFailures, setApprovalFailures] = useState<ApprovalFailure[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);

  const paymentById = useMemo(
    () =>
      new Map(
        (preview?.pendingPayments || []).map((payment) => [payment.id, payment])
      ),
    [preview]
  );

  const selectedPaymentIds = useMemo(
    () =>
      new Set(
        Object.values(selections).map((selection) => selection.paymentId)
      ),
    [selections]
  );

  const updateHeaderMapping = (
    header: string,
    systemField: BankStatementSystemField | ""
  ) => {
    setSelectedMapperId("");
    setMapping((previous) => {
      const mapperData = { ...previous.mapperData };
      Object.entries(mapperData).forEach(([field, mappedHeader]) => {
        if (mappedHeader.field === header || field === systemField) {
          delete mapperData[field as BankStatementSystemField];
        }
      });
      if (!systemField) {
        return { ...previous, mapperData };
      }
      mapperData[systemField] = { type: "direct", field: header };
      return { ...previous, mapperData };
    });
  };

  const handleFile = (nextFile: File) => {
    setFile(nextFile);
    setHeaders([]);
    setSampleData({});
    setMapping({ ...emptyMapping, headerRow: uploadHeaderRow });
    setSavedMappers([]);
    setSelectedMapperId("");
    setMapperName("");
    setPreview(undefined);
    setSelections({});
    setApprovalFailures([]);
  };

  const inspectFile = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const [inspectionResponse, mapperResponse] = await Promise.all([
        inspectBankStatement(file, uploadHeaderRow),
        getBankStatementMappers(),
      ]);
      const inspection = inspectionResponse.data;
      setHeaders(inspection.headersInFile || []);
      setSampleData(inspection.sampleData || {});
      setMapping({
        ...emptyMapping,
        headerRow: inspection.headerRow,
        mapperData: inspection.prefilledMapperData || {},
      });
      setSavedMappers(mapperResponse.data || []);
      setSelectedMapperId("");
      setMapperName("");
      setApprovalFailures([]);
      setStep("MAP");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorRaw ||
          error?.message ||
          "We couldn't read that bank statement"
      );
    } finally {
      setLoading(false);
    }
  };

  const applySavedMapper = (mapperId: string) => {
    setSelectedMapperId(mapperId);
    setMapperName("");
    const savedMapper = savedMappers.find((mapper) => mapper.id === mapperId);
    if (!savedMapper) return;

    const missingHeaders: string[] = [];
    const mapperData = Object.entries(savedMapper.mapperData).reduce<
      BankStatementMapping["mapperData"]
    >((nextMapperData, [field, fieldMapping]) => {
      if (!headers.includes(fieldMapping.field)) {
        missingHeaders.push(fieldMapping.field);
        return nextMapperData;
      }
      nextMapperData[field as BankStatementSystemField] = fieldMapping;
      return nextMapperData;
    }, {});

    setMapping((previous) => ({ ...previous, mapperData }));
    if (missingHeaders.length) {
      toast.info(
        `${savedMapper.name} has ${missingHeaders.length} column${
          missingHeaders.length === 1 ? "" : "s"
        } that is not present in this statement. Review the mapping.`
      );
    }
  };

  const buildPreview = async ({
    preserveApprovalFailures = false,
  }: {
    preserveApprovalFailures?: boolean;
  } = {}) => {
    if (!file) return;
    setLoading(true);
    try {
      const response = await previewBankStatement({
        transactionId,
        mapping,
        file,
      });
      const nextPreview = response.data;
      if (mapperName.trim()) {
        const mapperResponse = await saveBankStatementMapper({
          name: mapperName.trim(),
          mapperData: mapping.mapperData,
        });
        const savedMapper = mapperResponse.data;
        setSavedMappers((previous) => [...previous, savedMapper]);
        setSelectedMapperId(savedMapper.id);
        setMapperName("");
      }
      setPreview(nextPreview);
      const initialSelections: Record<string, Selection> = {};
      nextPreview.rows.forEach((row) => {
        if (
          row.status === "ELIGIBLE" &&
          row.claim &&
          row.candidatePaymentIds[0]
        ) {
          initialSelections[row.rowId] = {
            paymentId: row.candidatePaymentIds[0],
            acknowledgeMismatch: false,
          };
        }
      });
      setSelections(initialSelections);
      if (!preserveApprovalFailures) setApprovalFailures([]);
      setStep("REVIEW");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorRaw ||
          error?.message ||
          "We couldn't create the reconciliation preview"
      );
    } finally {
      setLoading(false);
    }
  };

  const updateSelection = (row: BankStatementPreviewRow, paymentId: string) => {
    setSelections((previous) => {
      const next = { ...previous };
      if (!paymentId) {
        delete next[row.rowId];
        return next;
      }
      next[row.rowId] = { paymentId, acknowledgeMismatch: false };
      return next;
    });
  };

  const selectedPayment = (row: BankStatementPreviewRow) =>
    selections[row.rowId]
      ? paymentById.get(selections[row.rowId].paymentId)
      : undefined;

  const requiresAcknowledgement = (row: BankStatementPreviewRow) => {
    const payment = selectedPayment(row);
    if (!payment || row.amount === null) return false;
    return (
      !statementReferenceIncludesPaymentReference(
        row.referenceId,
        payment.referenceId
      ) || Math.abs(row.amount - payment.amount) >= 0.0001
    );
  };

  const acknowledgementRows =
    preview?.rows.filter(
      (row) => selections[row.rowId] && requiresAcknowledgement(row)
    ) || [];
  const acknowledgementLabel = [
    "I reviewed the reference ID or amount differences for statement row",
    acknowledgementRows.length === 1 ? "" : "s",
    " ",
    acknowledgementRows.map((row) => row.sourceRow).join(", "),
    " and approve these manual pairings",
  ].join("");
  const hasUnconfirmedMismatch = acknowledgementRows.some(
    (row) => !selections[row.rowId].acknowledgeMismatch
  );

  const setMismatchAcknowledgement = (acknowledgeMismatch: boolean) => {
    setSelections((previous) => {
      const next = { ...previous };
      acknowledgementRows.forEach((row) => {
        if (next[row.rowId]) {
          next[row.rowId] = {
            ...next[row.rowId],
            acknowledgeMismatch,
          };
        }
      });
      return next;
    });
  };

  const confirmApproval = async () => {
    if (!preview) return;
    const selectedRows = preview.rows.filter((row) => selections[row.rowId]);
    const missingClaim = selectedRows.find((row) => !row.claim);
    if (missingClaim) {
      toast.error("Upload the statement again before approving this row");
      return;
    }
    if (
      selectedRows.some(
        (row) =>
          requiresAcknowledgement(row) &&
          !selections[row.rowId].acknowledgeMismatch
      )
    ) {
      toast.error("Confirm every manually paired difference before approval");
      return;
    }
    setApproving(true);
    try {
      const response = await approveBankStatement({
        transactionId,
        selections: selectedRows.map((row) => ({
          rowId: row.rowId,
          claim: row.claim!,
          paymentId: selections[row.rowId].paymentId,
          acknowledgeMismatch: selections[row.rowId].acknowledgeMismatch,
        })),
      });
      await onSuccess();
      const { approvedPaymentIds, failedGroups } = response.data;
      if (failedGroups.length) {
        setApprovalFailures(failedGroups);
        await buildPreview({ preserveApprovalFailures: true });
        toast.warning(
          `${approvedPaymentIds.length} payment${
            approvedPaymentIds.length === 1 ? " was" : "s were"
          } approved. Review the remaining payments.`
        );
        return;
      }
      toast.success(
        `${approvedPaymentIds.length} payment${
          approvedPaymentIds.length === 1 ? "" : "s"
        } approved successfully`
      );
      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorRaw ||
          error?.message ||
          "We couldn't approve the selected payments"
      );
    } finally {
      setApproving(false);
    }
  };

  const availablePayments = (row: BankStatementPreviewRow) =>
    (preview?.pendingPayments || []).filter(
      (payment) =>
        !selectedPaymentIds.has(payment.id) ||
        selections[row.rowId]?.paymentId === payment.id
    );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      menuTabs: ["filterMenuTab"],
      flex: 1,
      minWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    []
  );

  const reconciliationColumnDefs = useMemo<ColDef<BankStatementPreviewRow>[]>(
    () => [
      {
        headerName: "",
        pinned: "left",
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        flex: 0,
        sortable: false,
        filter: false,
        menuTabs: [],
        cellClass: "cell-center",
        cellRenderer: (params: { data?: BankStatementPreviewRow }) => {
          const row = params.data;
          if (!row) return null;
          const canPair =
            Boolean(row.claim) &&
            !["EXCLUDED_DEBIT", "INVALID", "ALREADY_APPROVED"].includes(
              row.status
            );
          return canPair ? (
            <CheckBoxWithLabel
              id={`approve-${row.rowId}`}
              label={`Select statement row ${row.sourceRow}`}
              labelclassname="sr-only"
              checked={Boolean(selections[row.rowId])}
              onChange={(event) =>
                updateSelection(
                  row,
                  event.target.checked ? row.candidatePaymentIds[0] || "" : ""
                )
              }
              disabled={!row.candidatePaymentIds[0] && !selections[row.rowId]}
            />
          ) : null;
        },
      },
      {
        headerName: "Statement row",
        pinned: "left",
        minWidth: 120,
        valueGetter: (params) =>
          params.data ? `#${params.data.sourceRow}` : "",
        cellRenderer: (params: { data?: BankStatementPreviewRow }) =>
          params.data ? (
            <VStack className="justify-center gap-1 py-2 text-xs2 text-secondaryText">
              <span>#{params.data.sourceRow}</span>
              <span>
                {params.data.paymentDate
                  ? formatDisplayDate(params.data.paymentDate)
                  : "No date"}
              </span>
            </VStack>
          ) : null,
      },
      {
        field: "referenceId",
        headerName: "Reference ID",
        minWidth: 170,
        cellRenderer: (params: { value?: string }) => (
          <span className="font-medium text-primaryText">
            {params.value || "Not provided"}
          </span>
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        minWidth: 130,
        valueFormatter: (params) =>
          params.value === null
            ? "Invalid"
            : formatCurrency(params.value, currencyType),
        cellClass: "cell-right font-medium tabular-nums text-primaryText",
        headerClass: "ag-right-aligned-header",
      },
      {
        colId: "selectedPaymentId",
        headerName: "Matched payment",
        minWidth: 280,
        flex: 2,
        editable: (params: any) =>
          Boolean(params.data?.claim) &&
          !["EXCLUDED_DEBIT", "INVALID", "ALREADY_APPROVED"].includes(
            params.data?.status
          ),
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: false,
        cellClass: "flex items-center",
        cellEditorParams: (params: any) => ({
          values: [
            "",
            ...availablePayments(params.data).map((payment) => payment.id),
          ],
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          valueListMaxWidth: 440,
          formatValue: (paymentId: string) => {
            const payment = paymentById.get(paymentId);
            return payment
              ? paymentLabel(payment, currencyType)
              : "Choose pending payment";
          },
        }),
        valueGetter: (params: any) =>
          params.data ? selections[params.data.rowId]?.paymentId || "" : "",
        valueFormatter: (params) => {
          const payment = paymentById.get(params.value);
          return payment
            ? paymentLabel(payment, currencyType)
            : "Choose pending payment";
        },
        valueSetter: (params: any) => {
          updateSelection(params.data, params.newValue || "");
          params.data.selectedPaymentId = params.newValue || "";
          return true;
        },
      },
      {
        field: "reasons",
        headerName: "Match evidence",
        minWidth: 200,
        cellRenderer: (params: { value?: string[] }) => (
          <VStack className="justify-center gap-1 py-2 text-xs2 text-secondaryText">
            {(params.value || []).map((reason) => (
              <span key={reason}>{reason}</span>
            ))}
          </VStack>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 140,
        cellRenderer: (params: { value?: string }) => (
          <span className="text-xs2 font-medium text-primaryText">
            {rowStatusText[params.value || ""] || params.value}
          </span>
        ),
      },
    ],
    [currencyType, preview, selections]
  );

  const unmatchedPaymentColumnDefs = useMemo<ColDef<BankStatementPayment>[]>(
    () => [
      {
        field: "employeeName",
        headerName: "Employee",
        minWidth: 200,
        cellRenderer: (params: { data?: BankStatementPayment }) =>
          params.data ? (
            <VStack className="justify-center gap-1 py-2">
              <span className="font-medium text-primaryText">
                {params.data.employeeName}
              </span>
              <span className="text-xs text-secondaryText">
                {params.data.hrId || "No employee ID"}
              </span>
            </VStack>
          ) : null,
      },
      { field: "requestId", headerName: "Request", minWidth: 160 },
      { field: "paymentType", headerName: "Payment type", minWidth: 150 },
      {
        field: "amount",
        headerName: "Amount",
        minWidth: 150,
        valueFormatter: (params) => formatCurrency(params.value, currencyType),
        cellClass: "cell-right font-medium tabular-nums text-primaryText",
        headerClass: "ag-right-aligned-header",
      },
      { field: "referenceId", headerName: "Reference ID", minWidth: 190 },
    ],
    [currencyType]
  );

  return (
    <VStack className="w-full max-h-[calc(100vh-32px)] gap-0 bg-white">
      <HStack className="items-start justify-between gap-4 border-b border-borderColor p-6">
        <VStack className="min-w-0 gap-1">
          <h2 className="text-base leading-6 font-semibold text-headerColor">
            Reconcile bank statement
          </h2>
          <p className="text-sm leading-5 text-secondaryText">
            {step === "UPLOAD"
              ? "Upload a company bank statement to verify pending exercise payments."
              : step === "MAP"
              ? "Map the statement columns before reviewing payment matches."
              : "Review matched payments and approve the selected rows."}
          </p>
        </VStack>
        <button
          type="button"
          aria-label="Close reconciliation dialog"
          onClick={onClose}
          className="cursor-pointer text-lg font-medium disabled:cursor-not-allowed disabled:opacity-50"
          disabled={loading || approving}
        >
          X
        </button>
      </HStack>

      <VStack className="min-h-0 flex-1 gap-6 overflow-y-auto px-6 py-5">
        {step === "UPLOAD" && (
          <VStack className="mx-auto w-full max-w-2xl gap-6 py-2">
            <FileDropzone
              selectedFile={file}
              onFileSelect={handleFile}
              onClear={() => {
                setFile(undefined);
                setHeaders([]);
                setSampleData({});
                setMapping(emptyMapping);
                setSavedMappers([]);
                setSelectedMapperId("");
                setMapperName("");
                setPreview(undefined);
                setSelections({});
                setApprovalFailures([]);
              }}
              acceptedFileExtensions={[".xlsx", ".xls"]}
              maxFileSize={10 * 1024 * 1024}
              instructionText="Bank statement Excel file (.xlsx or .xls)"
              disabled={loading}
              inputId="bank-statement-file"
            />
            <HStack className="mx-auto w-full max-w-xl items-center justify-between gap-3 rounded-lg border border-inputBorder bg-slate-light px-4 py-3">
              <HStack className="min-w-0 items-center gap-3">
                <VStack className="min-w-0">
                  <Label className="!mb-0 text-xs2 font-semibold text-headerColor">
                    Header row in statement
                  </Label>
                  <p className="text-xs text-secondaryText">
                    Specify which row contains column titles (default: 1)
                  </p>
                </VStack>
              </HStack>
              <HStack className="shrink-0 items-center overflow-hidden rounded-lg border border-inputBorder bg-white">
                <IconCTAButton
                  aria-label="Decrease header row"
                  buttonType={IconButtonType.NONE}
                  className="!rounded-none !border-0 text-secondaryText hover:bg-slate-light disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={loading || uploadHeaderRow <= 1}
                  iconName="material-symbols:remove-rounded"
                  iconOnly
                  iconSize={IconSize.M}
                  onClick={() =>
                    setUploadHeaderRow((currentRow) =>
                      Math.max(1, currentRow - 1)
                    )
                  }
                />
                <Input
                  id="bank-statement-upload-header-row"
                  aria-label="Header row"
                  className="h-10 w-10 !border-0 !bg-white p-0 text-center text-xs2 font-semibold text-headerColor focus:ring-0"
                  type="number"
                  min={1}
                  step={1}
                  value={uploadHeaderRow}
                  onChange={(event) =>
                    setUploadHeaderRow(
                      Math.max(1, Number(event.target.value) || 1)
                    )
                  }
                  disabled={loading}
                />
                <IconCTAButton
                  aria-label="Increase header row"
                  buttonType={IconButtonType.NONE}
                  className="!rounded-none !border-0 text-secondaryText hover:bg-slate-light disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={loading}
                  iconName="material-symbols:add-rounded"
                  iconOnly
                  iconSize={IconSize.M}
                  onClick={() =>
                    setUploadHeaderRow((currentRow) => currentRow + 1)
                  }
                />
              </HStack>
            </HStack>
            <Instructions
              data={[
                "Only .xlsx and .xls bank statement files are supported.",
                "The workbook is used only for this review and is not stored.",
              ]}
            />
          </VStack>
        )}

        {step === "MAP" && (
          <VStack className="gap-4">
            <HStack className="items-start justify-between gap-4">
              <VStack className="gap-1">
                <h3 className="text-base leading-6 font-semibold text-headerColor">
                  Map statement columns
                </h3>
                <p className="text-sm text-secondaryText">
                  Choose the payment details field for each Excel header. Only
                  reference ID and amount are required.
                </p>
              </VStack>
              <span className="shrink-0 text-xs2 font-medium text-secondaryText">
                Header row: {mapping.headerRow}
              </span>
            </HStack>
            <HStack className="flex-wrap gap-4">
              <VStack className="min-w-64 flex-1 gap-1">
                <Label
                  htmlFor="bank-statement-saved-mapper"
                  className="!mb-0 text-xs2 text-primaryText"
                >
                  Use an existing map
                </Label>
                <Select
                  id="bank-statement-saved-mapper"
                  options={savedMappers}
                  value={selectedMapperId}
                  placeholder="Select saved map"
                  disabled={loading || savedMappers.length === 0}
                  textGetter={(mapper) => mapper.name}
                  valueGetter={(mapper) => mapper.id}
                  onChange={(event) => applySavedMapper(event.target.value)}
                />
              </VStack>
              <VStack className="min-w-64 flex-1 gap-1">
                <Label
                  htmlFor="bank-statement-mapper-name"
                  className="!mb-0 text-xs2 text-primaryText"
                >
                  Config template name
                </Label>
                <Input
                  id="bank-statement-mapper-name"
                  value={mapperName}
                  maxLength={191}
                  placeholder="Save the current mapping"
                  disabled={loading}
                  onChange={(event) => setMapperName(event.target.value)}
                />
                <p className="text-xs text-secondaryText">
                  Saved when you review the statement.
                </p>
              </VStack>
            </HStack>
            <div className="max-h-[420px] overflow-auto rounded-lg border border-borderColor">
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-slate-light">
                  <tr className="border-b border-borderColor">
                    <th className="w-1/2 px-4 py-3 text-left text-xs2 font-semibold text-secondaryText">
                      Excel header
                    </th>
                    <th className="w-1/2 px-4 py-3 text-left text-xs2 font-semibold text-secondaryText">
                      Payment details field
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {headers.map((header) => {
                    const selectedField = mappedSystemField(mapping, header);
                    return (
                      <tr
                        key={header}
                        className="border-b border-dashed border-borderColor last:border-b-0"
                      >
                        <td className="px-4 py-3 align-middle">
                          <Box>
                            <p className="text-xs2 font-medium text-primaryText">
                              {header}
                            </p>
                            <p className="mt-1 text-xs text-secondaryText">
                              Example: {sampleValue(sampleData[header])}
                            </p>
                          </Box>
                        </td>
                        <td className="p-2 align-middle">
                          <SearchDropDown
                            options={[
                              "--IGNORE--",
                              ...Object.keys(bankStatementFieldLabels),
                            ]}
                            value={selectedField || "--IGNORE--"}
                            placeholder="Select payment field"
                            className="w-full"
                            getOptionLabel={(field) => {
                              if (field === "--IGNORE--")
                                return "Ignore this column";
                              const paymentField =
                                field as BankStatementSystemField;
                              return `${
                                bankStatementFieldLabels[paymentField]
                              }${
                                paymentField === "referenceId" ||
                                paymentField === "amount"
                                  ? " (required)"
                                  : ""
                              }`;
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option === value
                            }
                            onChange={(_, value) =>
                              updateHeaderMapping(
                                header,
                                value === "--IGNORE--"
                                  ? ""
                                  : (value as BankStatementSystemField)
                              )
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </VStack>
        )}

        {step === "REVIEW" && preview && (
          <VStack className="gap-6">
            {approvalFailures.length > 0 && (
              <HStack
                role="alert"
                className="items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
              >
                <Icon
                  icon="material-symbols:warning-amber-rounded"
                  width={IconSize.M}
                  className="mt-0.5 shrink-0 text-amber-800"
                  aria-hidden="true"
                />
                <VStack className="gap-1">
                  <p className="text-sm font-medium text-amber-800">
                    Some payments still need review
                  </p>
                  <p className="text-sm text-amber-800">
                    {approvalFailures
                      .map((failure) =>
                        failure.paymentType === "exercise price"
                          ? "Exercise price payments"
                          : "Tax price payments"
                      )
                      .join(" and ")}{" "}
                    could not be approved. The statement preview has been
                    refreshed. Review the remaining rows and try again.
                  </p>
                </VStack>
              </HStack>
            )}
            <VStack className="gap-4">
              <HStack className="items-center justify-between gap-4">
                <VStack className="gap-1">
                  <h3 className="text-base leading-6 font-semibold text-headerColor">
                    Statement rows
                  </h3>
                  <p className="text-sm text-secondaryText">
                    Select the payments that should be approved from this
                    statement.
                  </p>
                </VStack>
                <HStack className="shrink-0 flex-wrap justify-end gap-3 text-xs2 text-secondaryText">
                  <span>{preview.summary.eligible} ready</span>
                  <span>{preview.summary.needsReview} review</span>
                  <span>{preview.summary.unmatched} unmatched</span>
                  <span>{preview.summary.excludedDebit} excluded</span>
                </HStack>
              </HStack>
              <div className="bank-statement-reconciliation-grid bulk-state-grid h-[520px] min-h-0 w-full overflow-x-auto rounded-lg border border-borderColor ag-theme-material">
                <AgGridReact<BankStatementPreviewRow>
                  rowData={preview.rows}
                  columnDefs={reconciliationColumnDefs}
                  defaultColDef={defaultColDef}
                  getRowId={(params) => params.data.rowId}
                  getRowHeight={() => 64}
                  suppressRowTransform
                  suppressMenuHide
                  alwaysShowHorizontalScroll
                  singleClickEdit
                  stopEditingWhenCellsLoseFocus
                  pagination
                  paginationPageSize={50}
                  rowClass="border-t border-dashed hover:bg-slate-50"
                  overlayNoRowsTemplate="<span>No statement rows found</span>"
                />
              </div>
              {acknowledgementRows.length > 0 && (
                <CheckBoxWithLabel
                  id="acknowledge-manual-bank-statement-pairings"
                  label={acknowledgementLabel}
                  labelclassname="ml-2 text-xs2 text-primaryText"
                  checked={acknowledgementRows.every(
                    (row) => selections[row.rowId].acknowledgeMismatch
                  )}
                  onChange={(event) =>
                    setMismatchAcknowledgement(event.target.checked)
                  }
                />
              )}
            </VStack>
            <VStack className="gap-3">
              <VStack className="gap-1">
                <h3 className="text-base leading-6 font-semibold text-headerColor">
                  Pending payments not found in statement
                </h3>
                <p className="text-sm text-secondaryText">
                  These pending payments were not matched to a statement row.
                </p>
              </VStack>
              <div className="bank-statement-reconciliation-grid bulk-state-grid h-[280px] min-h-0 w-full overflow-x-auto rounded-lg border border-borderColor ag-theme-material">
                <AgGridReact<BankStatementPayment>
                  rowData={preview.unmatchedPendingPayments}
                  columnDefs={unmatchedPaymentColumnDefs}
                  defaultColDef={defaultColDef}
                  getRowId={(params) => params.data.id}
                  getRowHeight={() => 56}
                  suppressRowTransform
                  suppressMenuHide
                  alwaysShowHorizontalScroll
                  pagination
                  paginationPageSize={25}
                  rowClass="border-t border-dashed hover:bg-slate-50"
                  overlayNoRowsTemplate="<span>All pending payments were matched</span>"
                />
              </div>
            </VStack>
          </VStack>
        )}
      </VStack>

      <HStack className="items-center justify-between gap-3 border-t border-borderColor px-6 py-4">
        {step === "MAP" ? (
          <TertiaryCTAButton
            buttonSize={ButtonSize.MEDIUM}
            onClick={() => setStep("UPLOAD")}
            disabled={loading}
          >
            Back
          </TertiaryCTAButton>
        ) : (
          <TertiaryCTAButton
            buttonSize={ButtonSize.MEDIUM}
            onClick={onClose}
            disabled={loading || approving}
          >
            Cancel
          </TertiaryCTAButton>
        )}
        {step === "MAP" && (
          <PrimaryCTAButton
            buttonSize={ButtonSize.MEDIUM}
            onClick={() => buildPreview()}
            loading={loading}
            loadingText="Preparing preview..."
          >
            Review statement
          </PrimaryCTAButton>
        )}
        {step === "UPLOAD" && (
          <PrimaryCTAButton
            buttonSize={ButtonSize.MEDIUM}
            onClick={inspectFile}
            disabled={!file}
            loading={loading}
            loadingText="Preparing preview..."
          >
            Preview statement
          </PrimaryCTAButton>
        )}
        {step === "REVIEW" && (
          <Tooltip
            text="Confirm the manual pairings first"
            disabled={!hasUnconfirmedMismatch}
            className="w-max"
          >
            <HStack className="shrink-0">
              <PrimaryCTAButton
                buttonSize={ButtonSize.MEDIUM}
                onClick={confirmApproval}
                disabled={
                  !Object.keys(selections).length || hasUnconfirmedMismatch
                }
                loading={approving}
                loadingText="Approving payments..."
              >
                Approve selected payments
              </PrimaryCTAButton>
            </HStack>
          </Tooltip>
        )}
      </HStack>
    </VStack>
  );
}
